import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { sendOtp, OtpLogin } from '../../api/action/user';
import {
  ArrowLeft,
  Check,
  Clock,
  AlertCircle,
  Loader,
  Smartphone,
  Lock,
  FileText,
  Printer,
} from 'lucide-react';
import { formatAcademyDateTime, formatCurrency, formatPhoneNumber } from '../../utils/format';

const InvoicePayment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { phoneNumber, invoiceId, amount, order, user, invoice, originalPaymentMethod } = location.state || {};
  const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);
  const authUser = useSelector((state) => state?.auth?.data) || {};
  const companyState = useSelector((state) => state.companyReducer.company);
  const companyId = companyState?.primaryId;
  const step = 'otp';
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const countryCode = user?.countryCode || '+91';
  const shouldSkipOtp = Boolean(isAuthenticated || authUser?.primaryId || authUser?.email);
  const displayPhoneNumber = formatPhoneNumber(phoneNumber, countryCode || companyState);

  // Auto-send OTP when component mounts if phone number is available
  const sendOTP = useCallback(async () => {
    setIsLoading(true);
    setErrors({});
    try {
      const response = await dispatch(sendOtp({
        phoneNumber: phoneNumber.replace('+', ''),
        company: { primaryId: companyId }
      }));

      // Check if response exists and has the correct structure
      if (response?.status === "Success" || response?.data) {
        // For successful response
        setOtpSent(true);
        setCountdown(30);
        setErrors({}); // Clear any previous errors

        // If you need to show success message
        // setSuccessMessage('OTP sent successfully!');
      }
    } catch (error) {
      if (error.response?.data) {
        const apiError = error.response.data;
        setErrors({
          otp: apiError.message || 'Failed to send OTP. Please try again.'
        });
      } else {
        setErrors({
          otp: error.message || 'Failed to send OTP. Please try again.'
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, phoneNumber, companyId]);

  useEffect(() => {
    if (!phoneNumber) {
      navigate(invoiceId ? `/receipt/view/${invoiceId}` : '/my-bookings', { replace: true });
      return;
    }

    if (shouldSkipOtp) {
      navigate('/invoice-payment', {
        replace: true,
        state: {
          invoiceId,
          amount,
          order,
          user,
          invoice,
          originalPaymentMethod
        }
      });
      return;
    }

    sendOTP();
  }, [phoneNumber, shouldSkipOtp, navigate, invoiceId, amount, order, user, invoice, originalPaymentMethod, sendOTP]);

  // Countdown timer for OTP resend
  useEffect(() => {
    if (countdown > 0 && otpSent) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown, otpSent]);

  const handleOTPChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };
  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setErrors({ otp: 'Please enter complete 6-digit OTP' });
      return;
    }
    setErrors({});
    setIsLoading(true);

    try {
      const response = await dispatch(OtpLogin({
        phoneNumber: phoneNumber.replace('+', ''),
        dynamicAttributes: [{
          name: "Phone_otp",
          value: otpString
        }],
        company: { primaryId: companyId }
      }));

      // Handle response based on API structure
      if (response?.status === "Success" || response?.data) {
        // Successful OTP verification
        await new Promise(resolve => setTimeout(resolve, 1500));

        navigate('/invoice-payment', {
          state: {
            invoiceId: order?.invoiceId,
            amount: order?.amount || 0,
            order,
            user,
            invoice,
            originalPaymentMethod
          }
        });
      } else if (response?.status === "Failed" || response?.code >= 400) {
        // Handle API error response
        const errorMessage = response?.message || 'OTP verification failed';
        throw new Error(errorMessage);
      } else {
        throw new Error('OTP verification failed');
      }
    } catch (err) {
      // Handle different error formats
      if (err.response?.data) {
        const apiError = err.response.data;
        setErrors({
          otp: apiError.message || 'Invalid OTP. Please try again.'
        });
      } else if (err.message) {
        setErrors({ otp: err.message });
      } else {
        setErrors({ otp: 'Invalid OTP. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleNewPayment = () => {
    navigate(invoiceId ? `/receipt/view/${invoiceId}` : '/my-bookings');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Add top padding to account for fixed header */}
      <div className="pt-16"> {/* Adjust this value based on your header height */}
        <div className="max-w-5xl mx-auto px-4">
          {/* Header - Matching ViewInvoice style */}
          <div className="bg-white rounded-xl shadow-sm border mb-8">
            <div className="px-8 py-6 border-b border-gray-100">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleBack}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                      {step === 'otp' ? 'Verify Payment' : 'Payment Complete'}
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                      {step === 'otp' ? (
                        <>
                          <Clock size={16} className="text-gray-500" />
                          <span className="px-3 py-1 rounded-full text-sm font-medium border bg-yellow-100 text-yellow-800 border-yellow-200">
                            Pending Verification
                          </span>
                        </>
                      ) : (
                        <>
                          <Check size={16} className="text-gray-500" />
                          <span className="px-3 py-1 rounded-full text-sm font-medium border bg-green-100 text-green-800 border-green-200">
                            Payment Successful
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {step === 'success' && (
                    <>
                      <button
                        onClick={handlePrintReceipt}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg flex items-center gap-2 transition-colors"
                      >
                        <Printer size={16} />
                        Print Receipt
                      </button>
                      <button
                        onClick={handleNewPayment}
                        className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg flex items-center gap-2 transition-colors"
                      >
                        <FileText size={16} />
                        New Payment
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* OTP Verification */}
            {step === 'otp' && (
              <div className="bg-white rounded-xl shadow-sm border p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="md:col-span-2">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">OTP Verification</h2>

                    <div className="space-y-6">
                      <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <Smartphone size={24} className="text-blue-600" />
                        <div>
                          <p className="font-medium">We've sent a verification code to</p>
                          <p className="text-gray-700">{displayPhoneNumber}</p>
                        </div>
                      </div>

                      <form onSubmit={handleOTPSubmit} className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Enter 6-digit OTP</label>
                          <div className="flex gap-2 justify-start">
                            {otp.map((digit, index) => (
                              <input
                                key={index}
                                id={`otp-${index}`}
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={digit}
                                onChange={(e) => handleOTPChange(index, e.target.value)}
                                onKeyDown={(e) => handleOTPKeyDown(index, e)}
                                maxLength="1"
                                className="w-12 h-12 text-center text-xl font-bold border-2 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                                required
                                autoFocus={index === 0}
                              />
                            ))}
                          </div>
                          {errors.otp && (
                            <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                              <AlertCircle size={16} />
                              {errors.otp}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          <button
                            type="button"
                            onClick={sendOTP}
                            disabled={isLoading || countdown > 0}
                            className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                          >
                            {isLoading ? (
                              <Loader className="animate-spin h-4 w-4" />
                            ) : countdown > 0 ? (
                              `Resend code in ${countdown}s`
                            ) : (
                              "Didn't receive code? Resend"
                            )}
                          </button>

                          <button
                            type="submit"
                            disabled={isLoading}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                          >
                            {isLoading ? (
                              <Loader className="animate-spin h-5 w-5" />
                            ) : null}
                            Verify & Complete Payment
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Summary</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Invoice ID:</span>
                        <span className="font-medium">#{invoiceId || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment Method:</span>
                        <span className="font-medium">OTP Verification</span>
                      </div>
                      <hr className="my-2 border-gray-200" />
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total Amount:</span>
                        <span className="text-blue-600">{formatCurrency(amount, companyState)}</span>
                      </div>
                    </div>

                    <div className="mt-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 flex items-center gap-1">
                        <Lock size={12} />
                        Your payment is secured with end-to-end encryption
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Success */}
            {step === 'success' && (
              <div className="bg-white rounded-xl shadow-sm border p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="md:col-span-2">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Confirmation</h2>

                    <div className="space-y-6">
                      <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                        <Check size={24} className="text-green-600" />
                        <div>
                          <p className="font-medium">Your payment was successful!</p>
                          <p className="text-gray-700">A receipt has been sent to your registered email</p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h3 className="font-medium text-gray-900">Transaction Details</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Transaction ID</p>
                            <p className="font-medium">TXN-{Date.now().toString().slice(-8)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Payment Method</p>
                            <p className="font-medium">OTP Verified Payment</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Date & Time</p>
                            <p className="font-medium">{formatAcademyDateTime(new Date())}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Invoice Number</p>
                            <p className="font-medium">#{invoiceId || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-6 border border-green-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Amount Paid</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Invoice Total:</span>
                        <span className="font-medium">{formatCurrency(amount, companyState)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment Status:</span>
                        <span className="font-medium text-green-600">Completed</span>
                      </div>
                      <hr className="my-2 border-gray-200" />
                      <div className="flex justify-between text-lg font-bold">
                        <span>Amount Paid:</span>
                        <span className="text-green-600">{formatCurrency(amount, companyState)}</span>
                      </div>
                    </div>

                    <div className="mt-6">
                      <button
                        onClick={handlePrintReceipt}
                        className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-4 rounded-lg hover:from-green-600 hover:to-green-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <Printer size={16} />
                        Print Receipt
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePayment;
